import React, { useState, useEffect } from 'react';
import { InputType } from 'reactstrap/es/Input';
import classNames from 'classnames';
import 'css/partials/_bricks.NSCellInput.scss';
import NSInput from 'bricks/NSInput/NSInput';
import {
    numeralFormatter,
    numeralFormatterCurrency,
} from 'ns_libs/formatter';

export interface INSCellInputProps {
    value: any; // a percentage value should be passed in as decimal (e.g. 0.25 NOT 25 or 25%)
    handleUpdateValue: (updatedValue: string | number) => void;
    type?: InputType;
    placeholder?: string;
    customClassName?: string;
    labelClassName?: string;
    isRightAligned?: boolean;
    disabled?: boolean;
    inputWidth?: string;
    inputHeight?: string;
    numberType?: 'currency' | 'percentage';
    showCents?: boolean;
    inputClassName?: string;
}

const NSCellInput = ({
    value,
    handleUpdateValue,
    type,
    placeholder,
    customClassName,
    labelClassName,
    isRightAligned = false,
    disabled = false,
    inputWidth,
    inputHeight = '32px',
    numberType,
    showCents = false,
    inputClassName = '',
}: INSCellInputProps) => {
    let inputType: InputType = type || 'text';
    if (numberType) inputType = 'number'; // force inputType to number if numberType is provided

    const parseInputValue = (valueToParse: any) => {
        let parsedValue = valueToParse?.toString().trim() || '';
        if (numberType === 'percentage' && parsedValue !== '') {
            const percentageValue = Number(Number(valueToParse) * 100).toFixed(10); // to ensure precision
            parsedValue = String(Number(percentageValue)); // remove trailing zeros before setting to string
        }
        return parsedValue;
    };

    const formatDisplayValue = (valueToFormat: any) => {
        let displayValue = valueToFormat?.toString().trim() || '';
        if (valueToFormat === null || valueToFormat === undefined || displayValue === '') {
            displayValue = placeholder;
        } else if (numberType === 'percentage') {
            const percentageValue = Number(Number(valueToFormat) * 100).toFixed(10); // to ensure precision
            displayValue = `${numeralFormatter(String(Number(percentageValue)), '0.00')}%`;
        } else if (numberType === 'currency') {
            const numericValue = Number(valueToFormat);
            const showCentsFormat = showCents ? '$0,0.00' : '$0,0';
            displayValue = numeralFormatterCurrency(Math.abs(numericValue), showCentsFormat);
            if (numericValue < 0) {
                displayValue = `(${displayValue})`;
            }
        } else if (type === 'number') {
            const numericValue = Number(valueToFormat);
            const showCentsFormat = showCents ? '0,0.00' : '0,0';
            displayValue = numeralFormatterCurrency(Math.abs(numericValue), showCentsFormat);
        } else {
            displayValue = valueToFormat !== null && valueToFormat !== undefined ? valueToFormat : placeholder;
        }
        return displayValue;
    };

    const [displayValue, setDisplayValue] = useState<string>(formatDisplayValue(value));
    const [inputValue, setInputValue] = useState<string>(parseInputValue(value));
    const [forceBlur, setForceBlur] = useState(false);
    const [isFocused, setIsFocused] = useState(false);

    useEffect(() => {
        if (!isFocused) {
            if (parseInputValue(value) !== inputValue) {
                setInputValue(parseInputValue(value));
            }
            if (formatDisplayValue(value) !== displayValue) {
                setDisplayValue(formatDisplayValue(value));
            }
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, showCents]);

    useEffect(() => {
        if (forceBlur) {
            setTimeout(() => {
                setForceBlur(false);
            }, 1000);
        }
    }, [forceBlur]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        let parsedValue = e.target.value;
        if (inputType === 'number' && parsedValue !== '' && !parsedValue.includes('.')) {
            parsedValue = String(e.target.valueAsNumber);
        }
        setInputValue(parsedValue);
    };

    const checkValueUpdated = (updatedInputValue: string) => {
        const parsedInitialInputValue = parseInputValue(value);
        let valueDidUpdate = parsedInitialInputValue !== updatedInputValue;
        if (inputType === 'number') {
            valueDidUpdate = Number(parsedInitialInputValue).toFixed(10) !== Number(updatedInputValue).toFixed(10);
        }
        return valueDidUpdate;
    };

    const handleBlur = (updatedInputValue: string) => {
        const trimmedValue = updatedInputValue?.toString().trim() || '';
        if (checkValueUpdated(trimmedValue)) {
            let valueToUpdate: string | number = trimmedValue;
            if (inputType === 'number') {
                valueToUpdate = Number(trimmedValue);
                if (numberType === 'percentage') {
                    valueToUpdate /= 100;
                }
                valueToUpdate = Number(Number(valueToUpdate).toFixed(10));
            }

            handleUpdateValue(valueToUpdate);
            setDisplayValue(formatDisplayValue(String(valueToUpdate)));
        }
        setInputValue(trimmedValue);
        setIsFocused(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>, updatedInputValue: any) => {
        if (e.key === 'Enter') {
            let valueToFormat: string = updatedInputValue?.toString().trim() || '';
            if (numberType === 'percentage') {
                valueToFormat = String(Number(updatedInputValue) / 100);
            }
            setDisplayValue(formatDisplayValue(valueToFormat));
            setIsFocused(false);
            setForceBlur(true);
        } else if (e.key === 'Escape') {
            setInputValue(parseInputValue(value));
            setForceBlur(true);
        }
    };

    return (
        <div
            role="presentation"
            className={classNames(`NSCellInput text-primary align-middle ${customClassName}`, {
                'NSCellInput--disabled': disabled || forceBlur,
                'NSCellInput--focus': isFocused && !disabled && !forceBlur,
            })}
        >
            <NSInput
                id="NSCellInput"
                type={inputType || 'text'}
                name="NSCellInput"
                inputClassName={classNames(['NSCellInput__input text-primary', {
                    'ml-0': isRightAligned,
                    [inputClassName]: inputClassName,
                }])}
                value={inputValue}
                onChange={handleChange}
                onBlur={() => handleBlur(inputValue)}
                placeholder={placeholder}
                onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => handleKeyDown(e, inputValue)}
                isRightAligned={isRightAligned}
                inputWidth={inputWidth}
                inputHeight={inputHeight}
                onFocus={() => setIsFocused(true)}
                customClassName="px-1"
            />
            <label
                htmlFor="NSCellInput"
                className={classNames(`NSCellInput__label ${labelClassName} text-wrap font-weight-normal my-0 ml-1`, {
                    'text-right': isRightAligned,
                    'font-italic': placeholder === displayValue,
                })}
                style={{ minHeight: inputHeight }}
            >
                {displayValue}
            </label>
        </div>
    );
};

export default NSCellInput;
